import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Table, 
  Typography, 
  Spin, 
  Alert, 
  Button, 
  Space,
  Tag,
  Tooltip,
  Input,
  Statistic,
  Modal,
  DatePicker,
  Select
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_CONFIG } from '../../config/api';
import ReactECharts from 'echarts-for-react';

const { Title, Text } = Typography;
const { Search } = Input;

interface User {
  id: string;
  email: string;
  name?: string;
  is_active: boolean;
  email_verified_at?: string;
  last_login_at?: string;
  created_at: string;
}

const UserManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [useClientPagination, setUseClientPagination] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [verifiedUsers, setVerifiedUsers] = useState(0);
  const [trendOpen, setTrendOpen] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendUser, setTrendUser] = useState<User | null>(null);
  const [trendDailyStats, setTrendDailyStats] = useState<Array<{ date: string; total: number }>>([]);
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dayjs().subtract(6, 'month'), dayjs()]);
  const [daysWindow, setDaysWindow] = useState<number>(180);
  const [selectedTaskTypes, setSelectedTaskTypes] = useState<string[]>([]);
  const [allTaskTypes, setAllTaskTypes] = useState<string[]>([]);
  const [taskTypeStats, setTaskTypeStats] = useState<Array<{ task_type: string; daily_stats: Array<{ date: string; total: number }> }>>([]);

  const fetchUsers = useCallback(async (page: number = 1, pageSize: number = 20, search: string = '') => {
    setLoading(true);
    try {
      if (useClientPagination) {
        let filtered = allUsers;
        if (search) {
          const s = search.toLowerCase();
          filtered = allUsers.filter((u: User) =>
            (u.email || '').toLowerCase().includes(s) || (u.name || '').toLowerCase().includes(s)
          );
        }
        setTotal(filtered.length);
        const start = (page - 1) * pageSize;
        setUsers(filtered.slice(start, start + pageSize));
      } else {
        const skip = (page - 1) * pageSize;
        const params = new URLSearchParams({
          skip: String(skip),
          limit: String(pageSize),
        });
        if (search) {
          params.set('search', search);
        }
        const response = await fetch(`${API_CONFIG.TASK_MANAGER_BASE}/auth/admin/users?${params}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setUseClientPagination(true);
            const fullResponse = await fetch(`${API_CONFIG.TASK_MANAGER_BASE}/auth/admin/users?skip=0&limit=1000`, {
              method: 'GET',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
            });
            if (fullResponse.ok) {
              const fullData = await fullResponse.json();
              let filtered = Array.isArray(fullData) ? fullData : [];
              if (search) {
                const s = search.toLowerCase();
                filtered = filtered.filter((u: User) =>
                  (u.email || '').toLowerCase().includes(s) || (u.name || '').toLowerCase().includes(s)
                );
              }
              setAllUsers(filtered);
              setTotal(filtered.length);
              const start = (page - 1) * pageSize;
              setUsers(filtered.slice(start, start + pageSize));
            }
          } else {
            setUsers(data.users || []);
            setTotal(data.total || 0);
          }
          setError(null);
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }
    } catch (err) {
      setError('获取用户数据失败');
      console.error('获取用户数据失败:', err);
    } finally {
      setLoading(false);
    }
  }, [useClientPagination, allUsers]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.TASK_MANAGER_BASE}/auth/admin/users/stats`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setTotalUsers(data.total || 0);
        setActiveUsers(data.active || 0);
        setVerifiedUsers(data.verified || 0);
        return;
      }
    } catch {}
    try {
      const response = await fetch(`${API_CONFIG.TASK_MANAGER_BASE}/auth/admin/users?skip=0&limit=1000`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        const usersList = Array.isArray(data) ? data : (data.users || []);
        setTotalUsers(usersList.length);
        setActiveUsers(usersList.filter((u: User) =>
          u.last_login_at && dayjs(u.last_login_at).isAfter(dayjs().subtract(7, 'day'))
        ).length);
        setVerifiedUsers(usersList.filter((u: User) => u.email_verified_at).length);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchUsers(1, 20, '');
    fetchStats();
  }, [fetchUsers, fetchStats]);

  const handleTableChange = (paginationConfig: any) => {
    const { current, pageSize } = paginationConfig;
    setPagination({ current, pageSize });
    fetchUsers(current, pageSize, searchText);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchUsers(1, pagination.pageSize, value);
  };

  const userColumns = [
    {
      title: '邮箱地址',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => (
        <Space>
          <MailOutlined />
          <Text copyable>{email}</Text>
        </Space>
      )
    },
    {
      title: '用户名',
      dataIndex: 'name',
      key: 'name',
      render: (name: string | undefined) => 
        name ? <Text>{name}</Text> : <Text type="secondary">未设置</Text>
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (
        <Space>
          <CalendarOutlined />
          <Text>{dayjs(date).format('YYYY-MM-DD HH:mm')}</Text>
        </Space>
      ),
      sorter: (a: User, b: User) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix()
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      render: (date: string | undefined) => 
        date ? (
          <Space>
            <CalendarOutlined />
            <Text>{dayjs(date).format('YYYY-MM-DD HH:mm')}</Text>
          </Space>
        ) : (
          <Text type="secondary">从未登录</Text>
        ),
      sorter: (a: User, b: User) => {
        const aTime = a.last_login_at ? dayjs(a.last_login_at).unix() : 0;
        const bTime = b.last_login_at ? dayjs(b.last_login_at).unix() : 0;
        return bTime - aTime;
      },
      defaultSortOrder: 'descend' as const
    },
    {
      title: '邮箱验证',
      dataIndex: 'email_verified_at',
      key: 'email_verified_at',
      render: (verified: string | undefined) => (
        <Tag color={verified ? 'success' : 'warning'} icon={verified ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {verified ? '已验证' : '未验证'}
        </Tag>
      )
    },
    {
      title: '账户状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'error'} icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {isActive ? '活跃' : '禁用'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      render: (record: User) => (
        <Space>
          <Tooltip title="活跃趋势">
            <Button type="link" icon={<BarChartOutlined />} onClick={() => openTrendModal(record)}>
              活跃趋势
            </Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  const onRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) return;
    const validRange: [dayjs.Dayjs, dayjs.Dayjs] = [dates[0], dates[1]];
    setRange(validRange);
    const ms = Math.abs(validRange[1].startOf('day').valueOf() - validRange[0].startOf('day').valueOf());
    const days = Math.max(1, Math.floor(ms / (24 * 3600 * 1000)) + 1);
    setDaysWindow(days);
    if (trendUser) {
      fetchUserTrend(trendUser.id, days);
    }
  };

  const fetchUserTrend = async (userId: string, days: number) => {
    setTrendLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date(endDate.getTime() - days * 24 * 60 * 60 * 1000);
      
      const response = await fetch(`${API_CONFIG.TASK_MANAGER_BASE}/tasks?user_id=${userId}&start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`获取用户任务失败: ${response.status}`);
      }
      
      const tasks = await response.json();
      
      const dailyStats: { [date: string]: number } = {};
      const taskTypeStatsLocal: { [taskType: string]: { [date: string]: number } } = {};
      const taskTypes = new Set<string>();
      
      tasks.forEach((task: any) => {
        const date = task.created_at.split('T')[0];
        const taskType = task.task_type;
        
        taskTypes.add(taskType);
        dailyStats[date] = (dailyStats[date] || 0) + 1;
        
        if (!taskTypeStatsLocal[taskType]) {
          taskTypeStatsLocal[taskType] = {};
        }
        taskTypeStatsLocal[taskType][date] = (taskTypeStatsLocal[taskType][date] || 0) + 1;
      });
      
      const allDates: string[] = [];
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        allDates.push(d.toISOString().split('T')[0]);
      }
      
      setTrendDailyStats(allDates.map(date => ({
        date,
        total: dailyStats[date] || 0
      })));
      
      const taskTypesArray = Array.from(taskTypes);
      setAllTaskTypes(taskTypesArray);
      setSelectedTaskTypes(taskTypesArray);
      
      setTaskTypeStats(taskTypesArray.map(taskType => ({
        task_type: taskType,
        daily_stats: allDates.map(date => ({
          date,
          total: taskTypeStatsLocal[taskType]?.[date] || 0
        }))
      })));
      
    } catch (e) {
      console.error('获取用户趋势失败: ', userId, e);
      setTrendDailyStats([]);
      setTaskTypeStats([]);
      setAllTaskTypes([]);
      setSelectedTaskTypes([]);
    } finally {
      setTrendLoading(false);
    }
  };

  const openTrendModal = async (user: User) => {
    setTrendUser(user);
    setTrendOpen(true);
    await fetchUserTrend(user.id, daysWindow);
  };

  const getTrendOption = () => {
    const allDates = Array.from(new Set([
      ...trendDailyStats.map(d => d.date),
      ...taskTypeStats.flatMap(t => t.daily_stats.map(d => d.date))
    ])).sort();

    const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#fa8c16', '#13c2c2', '#eb2f96'];

    const series = [];
    
    if (selectedTaskTypes.length === 0 || selectedTaskTypes.length === allTaskTypes.length) {
      const totalData = allDates.map(date => {
        const stat = trendDailyStats.find(d => d.date === date);
        return stat ? stat.total : 0;
      });
      series.push({
        name: '总计',
        type: 'line',
        smooth: true,
        data: totalData,
        itemStyle: { color: '#1890ff' },
        lineStyle: { width: 3 }
      });
    }

    taskTypeStats
      .filter(t => selectedTaskTypes.includes(t.task_type))
      .forEach((taskType, index) => {
        const data = allDates.map(date => {
          const stat = taskType.daily_stats.find(d => d.date === date);
          return stat ? stat.total : 0;
        });
        series.push({
          name: taskType.task_type,
          type: 'line',
          smooth: true,
          data: data,
          itemStyle: { color: colors[(index + 1) % colors.length] }
        });
      });

    return {
      title: { 
        text: trendUser ? `${trendUser.email} 活跃趋势（${daysWindow}天）` : '活跃趋势', 
        left: 'center' 
      },
      tooltip: { 
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      legend: { 
        data: series.map(s => s.name), 
        top: 30,
        type: 'scroll'
      },
      xAxis: { 
        type: 'category', 
        data: allDates,
        axisLabel: { rotate: 45 }
      },
      yAxis: { 
        type: 'value',
        name: '提交数'
      },
      series: series,
      grid: { top: 80, bottom: 80 }
    };
  };

  if (error) {
    return (
      <Alert
        message="数据加载失败"
        description={error}
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  return (
    <div>
      <Title level={2}>用户管理</Title>
      
      <div style={{ marginBottom: 24 }}>
        <Space size="large">
          <Card>
            <Statistic
              title="总用户数"
              value={totalUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
          <Card>
            <Statistic
              title="活跃用户"
              value={activeUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
          <Card>
            <Statistic
              title="已验证邮箱"
              value={verifiedUsers}
              prefix={<MailOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Space>
      </div>

      <Card 
        title="用户列表" 
        extra={
          <Space>
            <Search
              placeholder="搜索邮箱或用户名"
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
              onChange={(e) => {
                if (!e.target.value) {
                  handleSearch('');
                }
              }}
            />
          </Space>
        }
      >
        <Table
          dataSource={users}
          columns={userColumns}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (t, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${t} 条`
          }}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        open={trendOpen}
        title={trendUser ? `${trendUser.email} 活跃趋势` : '活跃趋势'}
        onCancel={() => setTrendOpen(false)}
        footer={null}
        width={900}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ color: '#888' }}>时间范围：</span>
            <DatePicker.RangePicker
              allowClear={false}
              value={range}
              onChange={onRangeChange}
              presets={[
                { label: '近7天', value: [dayjs().subtract(7, 'day'), dayjs()] },
                { label: '近30天', value: [dayjs().subtract(30, 'day'), dayjs()] },
                { label: '近3个月', value: [dayjs().subtract(3, 'month'), dayjs()] },
                { label: '近6个月', value: [dayjs().subtract(6, 'month'), dayjs()] },
                { label: '近1年', value: [dayjs().subtract(12, 'month'), dayjs()] },
              ]}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#888' }}>任务类型：</span>
            <Select
              mode="multiple"
              allowClear
              placeholder="选择任务类型（默认显示所有）"
              style={{ width: 300 }}
              value={selectedTaskTypes}
              onChange={setSelectedTaskTypes}
              options={allTaskTypes.map(type => ({ label: type, value: type }))}
            />
          </div>
        </div>
        <div style={{ height: 420 }}>
          {trendLoading ? (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Spin />
            </div>
          ) : (
            <ReactECharts option={getTrendOption()} style={{ height: 360 }} />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default UserManagement; 
